import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Compass, Map, Wrench, TrendingUp } from 'lucide-react';
import SEO from '../components/SEO';
import KineticText from '../components/KineticText';
import { Reveal } from '../components/Reveal';
import LeadForm from '../components/LeadForm';

const POINTS = [
  { icon: Compass, title: 'Right Direction', text: 'Get clear on which career actually fits you.' },
  { icon: Map, title: 'Step-by-Step Roadmap', text: 'From where you are today to your target career.' },
  { icon: Wrench, title: 'Skills That Matter', text: 'Learn what employers actually look for.' },
  { icon: TrendingUp, title: 'Long-Term Growth', text: 'Support from school to career and beyond.' },
];

const Start = () => (
  <main data-testid="instagram-landing-page">
    <SEO
      title="Confused About Your Career? | CareerWithRaaj"
      description="Get the right direction, skills, and roadmap for your career. Career guidance and planning for students, graduates, and professionals."
    />

    <section className="relative overflow-hidden bg-navy pb-20 pt-32 md:pb-28 md:pt-40">
      <div className="bg-grid-dark absolute inset-0" />
      <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-coral/20 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-xs font-bold uppercase tracking-[0.25em] text-coral"
        >
          CareerWithRaaj
        </motion.p>
        <h1 className="mt-6 max-w-3xl font-display text-5xl font-extrabold tracking-tighter text-white md:text-6xl lg:text-7xl" data-testid="start-headline">
          <KineticText lines={['Confused About', { text: 'Your Career?', accent: true }]} delay={0.15} />
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.55 }}
          className="mt-6 max-w-xl text-base text-white/70 md:text-lg"
          data-testid="start-subheadline"
        >
          Get the right direction, skills, and roadmap for your career.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-9 flex flex-col gap-3 sm:flex-row"
        >
          <a
            href="#start-form"
            data-testid="start-get-roadmap-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-coral px-8 py-4 text-base font-semibold text-white transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-coral-deep"
          >
            Get Your Career Roadmap <ArrowRight className="h-4 w-4" />
          </a>
          <Link
            to="/book-consultation"
            data-testid="start-book-consultation-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/20 px-8 py-4 text-base font-semibold text-white transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 hover:border-white hover:bg-white/5"
          >
            Book a Consultation
          </Link>
        </motion.div>
      </div>
    </section>

    <section className="bg-white py-20 md:py-24">
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {POINTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <Reveal key={p.title} delay={0.06 * i}>
                <div className="h-full rounded-3xl border border-line bg-mist p-7" data-testid={`start-point-${i + 1}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-coral">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-navy">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>

        <div id="start-form" className="mx-auto mt-16 max-w-2xl scroll-mt-28">
          <Reveal>
            <div className="rounded-3xl border border-line bg-white p-8 shadow-card-hover md:p-10" data-testid="start-form-card">
              <h2 className="font-display text-2xl font-bold text-navy">Get Your Career Roadmap</h2>
              <p className="mt-2 text-sm text-slate-500">
                30 seconds. Tell us where you are — we'll show you the way forward.
              </p>
              <div className="mt-7">
                <LeadForm source="instagram-landing" buttonLabel="Get Your Career Roadmap" interestLabel="Career Interest" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  </main>
);

export default Start;
